/**
 * Modal para añadir comentario a un envío
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Alert,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CommentIcon from '@mui/icons-material/Comment';
import { TAM_MAX_OBSERVACIONES } from '../../constants';
import { estaEstadoBloqueado } from '../../helpers';

const ModalComentario = ({ open, onClose, envio, onSave, loading = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [comentario, setComentario] = useState('');
    const [error, setError] = useState('');

    const estadoBloqueado = envio ? estaEstadoBloqueado(envio.tracking_estado) : false;

    useEffect(() => {
        if (open && envio) {
            setComentario(envio.tracking_comentario || '');
            setError('');
        }
    }, [open, envio]);

    const handleChange = (e) => {
        const value = e.target.value;
        setComentario(value);

        if (value.length > TAM_MAX_OBSERVACIONES) {
            setError(`Máximo ${TAM_MAX_OBSERVACIONES} caracteres`);
        } else {
            setError('');
        }
    };

    const handleSave = () => {
        if (comentario.length > TAM_MAX_OBSERVACIONES) {
            return;
        }
        onSave(comentario.trim());
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    if (!envio) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    m: isMobile ? 0 : 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: '50%',
                            backgroundColor: '#fee',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <CommentIcon sx={{ color: '#cc071e', fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: '#212529',
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            }}
                        >
                            Comentario envío
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#6c757d',
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            }}
                        >
                            <strong>
                                {envio.serie}-{envio.albaran}
                            </strong>
                            {' · '}
                            {envio.nombre}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: '#6c757d',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : 'initial',
                }}
            >
                {estadoBloqueado && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Este envío está en estado bloqueado. Solo lectura.
                    </Alert>
                )}

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Escribe un comentario..."
                    value={comentario}
                    onChange={handleChange}
                    disabled={loading || estadoBloqueado}
                    inputProps={{
                        maxLength: TAM_MAX_OBSERVACIONES,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: estadoBloqueado ? '#f8f9fa' : '#fff',
                        },
                    }}
                    helperText={`${comentario.length} / ${TAM_MAX_OBSERVACIONES} caracteres`}
                />

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: '#6c757d',
                        borderColor: '#6c757d',
                        '&:hover': {
                            borderColor: '#495057',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                    }}
                >
                    Cerrar
                </Button>
                {!estadoBloqueado && (
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading || !!error}
                        fullWidth={isMobile}
                        startIcon={<SaveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: { xs: 2, sm: 2.5 },
                            py: { xs: 1.25, sm: 1 },
                            fontSize: { xs: 14, sm: 15 },
                            backgroundColor: '#cc071e',
                            '&:hover': {
                                backgroundColor: '#a30618',
                            },
                            '&:disabled': {
                                backgroundColor: '#6c757d',
                                color: '#fff',
                            },
                        }}
                    >
                        Guardar
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ModalComentario;
